'use client'

import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { animatedEmojiUrl } from '@/lib/animated-emoji'
import { hapticTap } from '@/lib/telegram/haptics'
import { cn } from '@/lib/utils'

// lottie-web + pako only load through this dynamic boundary, and only once the
// emoji is on screen — never in a route's initial JS.
const TgsPlayer = lazy(() => import('@/components/tgs-player').then((m) => ({ default: m.TgsPlayer })))

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(true) // static until proven otherwise
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = () => setReduced(mq.matches)
    sync()
    mq.addEventListener('change', sync)
    return () => mq.removeEventListener('change', sync)
  }, [])
  return reduced
}

function useDocumentVisible() {
  const [visible, setVisible] = useState(true)
  useEffect(() => {
    const sync = () => setVisible(document.visibilityState !== 'hidden')
    sync()
    document.addEventListener('visibilitychange', sync)
    return () => document.removeEventListener('visibilitychange', sync)
  }, [])
  return visible
}

// Telegram animated emoji. The static emoji occupies the exact box from the
// first paint (no layout shift); the TGS is layered on top when ready.
export function AnimatedEmoji({
  emoji,
  size = 96,
  loops = 2,
  playOnTap = true,
  autoPlay = true,
  className,
}: {
  emoji: string
  size?: number
  loops?: number
  playOnTap?: boolean
  autoPlay?: boolean
  className?: string
}) {
  const ref = useRef<HTMLSpanElement | null>(null)
  const reduced = usePrefersReducedMotion()
  const docVisible = useDocumentVisible()
  const [inView, setInView] = useState(false)
  const [everInView, setEverInView] = useState(false)
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const [replay, setReplay] = useState(0)

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(([e]) => {
      const v = !!e?.isIntersecting
      setInView(v)
      if (v) setEverInView(true)
    })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  const canAnimate = !reduced && !failed && everInView && (autoPlay || replay > 0)
  const active = inView && docVisible

  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={cn('relative inline-flex shrink-0 items-center justify-center select-none', className)}
      style={{ width: size, height: size }}
      onClick={
        playOnTap && !reduced && ready
          ? () => {
              hapticTap('light')
              setReplay((n) => n + 1)
            }
          : undefined
      }
    >
      <span
        className="leading-none transition-opacity duration-150"
        style={{ fontSize: Math.round(size * 0.78), opacity: ready ? 0 : 1 }}
      >
        {emoji}
      </span>
      {canAnimate ? (
        <span className="absolute inset-0">
          <Suspense fallback={null}>
            <TgsPlayer
              fileUrl={animatedEmojiUrl(emoji)}
              size={size}
              fill
              loop={loops}
              paused={!active}
              replayKey={replay}
              onReady={() => setReady(true)}
              onFail={() => {
                setFailed(true)
                setReady(false)
              }}
              fallback={null}
            />
          </Suspense>
        </span>
      ) : null}
    </span>
  )
}
